import { createPopper } from '@popperjs/core';
import { generateIdAttribute } from '../utils/_domFunctions';
import { createMediaQueryList } from '../utils/_breakpoints';

class mInfoBubble extends HTMLButtonElement {
    constructor() {
        super();
        this.mqlDesktop = createMediaQueryList('md');

        const bubbleSlot = this.querySelector('[data-slot="dialog"]');
        if (!bubbleSlot) {
            return;
        }
        bubbleSlot.remove();

        // Setup bubble
        this.bubbleEl = document.createElement('div');
        if (!this.bubbleEl.id) {
            this.bubbleEl.id = generateIdAttribute('bubble');
        }
        this.bubbleEl.role = 'dialog';
        this.bubbleEl.className = 'info-bubble';
        this.bubbleEl.append(...bubbleSlot.content.cloneNode(true).children);
        this.popperInstance = createPopper(this, this.bubbleEl);
        this.updatePlacement();
        this.isTooltipClicked = false;

        // Setup button
        this.buttonToggleState = false;
        this.setAttribute('aria-controls', this.bubbleEl.id);
        this.toggleDisplay(false);

        // Add button event listeners
        this.addEventListener('click', () => {
            this.isTooltipClicked = !this.isTooltipClicked;
            this.toggleDisplay(this.isTooltipClicked);
        });

        this.addEventListener('mouseenter', () => {
            this.inHover();
        });

        this.addEventListener('mouseleave', () => {
            this.inHover();
        });

        // Add bubble event listeners
        this.bubbleEl.querySelectorAll('[data-close]').forEach((el) => {
            el.addEventListener('click', () => {
                this.toggleDisplay(false);
                this.isTooltipClicked = false;
            });
        });
        this.bubbleEl.addEventListener('mouseleave', () => {
            if (!this.isTooltipClicked) {
                this.toggleDisplay(false);
            }
        });

        this.onClickOutside = (event) => {
            if (!this.isBubbleOpened()) return;
            if (event.target !== this && !this.bubbleEl.contains(event.target)) {
                this.isTooltipClicked = false;
                this.toggleDisplay(false);
            }
        };
        this.onKeydown = (event) => {
            if (!this.isBubbleOpened) return;
            if (event.key === 'Escape') {
                this.toggleDisplay(false);
            }
        };
        this.mqlDesktop.addEventListener('change', () => { this.updatePlacement(); });
    }

    connectedCallback() {
        window.addEventListener('click', this.onClickOutside, { passive: true });
        window.addEventListener('keydown', this.onKeydown, { passive: true });
    }

    disconnectedCallback() {
        window.removeEventListener('click', this.onClickOutside, { passive: true });
        window.removeEventListener('keydown', this.onKeydown, { passive: true });
    }

    isBubbleOpened() {
        return !!this.bubbleEl.parentElement;
    }

    toggleDisplay(force) {
        const enabled = force ?? !this.isBubbleOpened();

        if (enabled) {
            document.body.append(this.bubbleEl);
            this.setAttribute('aria-expanded', 'true');
            if (this.contains(document.activeElement)) {
                (this.bubbleEl.querySelector('.info-bubble-close-btn') || this.bubbleEl).focus();
            }
            this.popperInstance.update();
        } else {
            if (this.bubbleEl.contains(document.activeElement)) {
                this.focus();
            }
            this.setAttribute('aria-expanded', 'false');
            this.bubbleEl.remove();
        }

        // Toggle event listeners for optimization purpose
        this.popperInstance.setOptions((options) => ({
            ...options,
            modifiers: [
                ...options.modifiers,
                { name: 'eventListeners', enabled },
            ],
        }));
    }

    inHover() {
        if (this.isTooltipClicked) return;
        this.toggleDisplay(this.matches(':hover') || this.bubbleEl.matches(':hover'));
    }

    updatePlacement() {
        this.popperInstance.setOptions((options) => ({
            ...options,
            placement: this.mqlDesktop.matches ? 'right' : 'bottom-end',
        }));
    }
}

customElements.define('m-info-bubble', mInfoBubble, {
    extends: 'button',
});
